import React from "react";
import {
  Flex,
  Text,
  Image,
  Box,
  VStack,
  HStack,
  Divider,
} from "@chakra-ui/react";
import CategorySection from "./CategorySection";
import QRCode from "react-qr-code";
import { FaPepperHot, FaPhone, FaMapMarkerAlt, FaGlobe } from "react-icons/fa";

export default function PageOne() {
  return (
    <>
      {/* Column 1: Noodles & Extras */}
      <MenuColumn>
        <CategorySection
          category="Shanghai Noodles (Thick)"
          index={11}
          marginFlag={false}
        />
        <CategorySection
          category="Chow Mein (Thin)"
          index={10}
          marginFlag={false}
        />
        <CategorySection category="Extras" index={13} marginFlag={false} />
      </MenuColumn>

      {/* Column 2: Group Dinners */}
      <MenuColumn>
        <GroupDinnersSection />
      </MenuColumn>

      {/* Column 3: Combo For One */}
      <MenuColumn>
        <ComboForOneSection />
      </MenuColumn>

      {/* Column 4: Restaurant Info */}
      <MenuColumn>
        <RestaurantInfoSection />
      </MenuColumn>
    </>
  );
}

// Reusable column wrapper
function MenuColumn({ children }) {
  return (
    <Flex
      direction="column"
      width="25%"
      borderRight="2px solid"
      borderColor="red.600"
      bg="white"
      _last={{ borderRight: "none" }}
    >
      {children}
    </Flex>
  );
}

// Group Dinners Section
function GroupDinnersSection() {
  const groupDinnerItems = [
    { item: "Ginger Beef", number: "D1", spicy: true },
    { item: "Sweet & Sour Pork", number: "D2", spicy: false },
    { item: "Lemon Chicken", number: "D3", spicy: false },
    { item: "Dry Garlic Spareribs", number: "D4", spicy: false },
    { item: "Chicken Balls", number: "D5", spicy: false },
    { item: "Deep Fried Bubble Shrimp", number: "D6", spicy: false },
    { item: "Chicken Chop Suey", number: "D7", spicy: false },
    { item: "Chicken w/ Mixed Veggies", number: "D8", spicy: false },
    { item: "Chicken Chow Mein", number: "D9", spicy: false },
    { item: "Chicken Shanghai Noodles", number: "D10", spicy: false },
  ];

  const dinnerPackages = [
    { name: "Dinner For Two", price: 41.96, choices: 1 },
    { name: "Dinner For Three", price: 56.96, choices: 2 },
    { name: "Dinner For Four", price: 83.96, choices: 3 },
    {
      name: "Dinner For Six",
      price: 113.96,
      choices: 4,
      extras: ["Chicken Fried Rice x 2"],
    },
    {
      name: "Dinner For Eight",
      price: 143.96,
      choices: 5,
      extras: ["Chicken Fried Rice x 2", "Stir Fried Mixed Veggies x 2"],
    },
  ];

  return (
    <VStack spacing={2.5} p={4} align="stretch">
      {/* Header */}
      <Box textAlign="center" mb={1}>
        <Text
          fontSize="26px"
          fontWeight="800"
          color="red.700"
          letterSpacing="wide"
          textTransform="uppercase"
        >
          Group Dinners
        </Text>
        <Divider
          borderColor="red.600"
          borderWidth="2px"
          mt={2}
          width="60%"
          mx="auto"
        />
      </Box>

      {/* Included Items */}
      <Box border="2px solid" borderColor="red.600" borderRadius="md" p={2}>
        <Text
          fontSize="14px"
          fontWeight="700"
          color="red.700"
          mb={1.5}
          textAlign="center"
        >
          All Group Dinners Include
        </Text>
        <HStack spacing={2} align="start">
          <VStack spacing={0.5} align="start" flex="1">
            {["Spring Rolls", "Wonton Soup"].map((item) => (
              <HStack key={item} spacing={1.5}>
                <Box w="5px" h="5px" bg="red.600" borderRadius="full" />
                <Text fontSize="13px" color="gray.800">
                  {item}
                </Text>
              </HStack>
            ))}
          </VStack>
          <VStack spacing={0.5} align="start" flex="1">
            {["Chicken Fried Rice", "Stir Fried Mixed Veggies"].map((item) => (
              <HStack key={item} spacing={1.5}>
                <Box w="5px" h="5px" bg="red.600" borderRadius="full" />
                <Text fontSize="13px" color="gray.800">
                  {item}
                </Text>
              </HStack>
            ))}
          </VStack>
        </HStack>
      </Box>

      <Box>
        <HStack spacing={2} mb={1.5} align="stretch">
          <VStack spacing={1.5} flex="1">
            <DinnerPackageCard {...dinnerPackages[0]} />
            <DinnerPackageCard {...dinnerPackages[2]} />
          </VStack>
          <VStack spacing={1.5} flex="1">
            <DinnerPackageCard {...dinnerPackages[1]} />
            <DinnerPackageCard {...dinnerPackages[3]} />
          </VStack>
        </HStack>

        <VStack spacing={1.5}>
          <DinnerPackageCard {...dinnerPackages[4]} />
        </VStack>
      </Box>

      {/* Choices List - Single Column with larger fonts */}
      <Box border="2px solid" borderColor="red.600" borderRadius="md" p={3}>
        <Text
          fontWeight="700"
          fontSize="17px"
          color="red.700"
          mb={2}
          textAlign="center"
          textTransform="uppercase"
          letterSpacing="wide"
        >
          Group Dinner Choices
        </Text>
        <VStack spacing={1} align="start">
          {groupDinnerItems.map((item) => (
            <HStack key={item.number} spacing={2} w="full">
              <Text
                fontSize="15px"
                fontWeight="600"
                color="red.600"
                minW="30px"
              >
                {item.number}
              </Text>
              <Text fontSize="15px" color="gray.800">
                {item.item}
              </Text>
              {item.spicy && <FaPepperHot color="#DC2626" size={12} />}
            </HStack>
          ))}
        </VStack>
      </Box>
    </VStack>
  );
}

// Individual dinner package card
function DinnerPackageCard({ name, price, choices, extras }) {
  return (
    <Flex
      direction="column"
      border="2px solid"
      borderColor="red.600"
      borderRadius="md"
      p={2}
      w="full"
      minH="120px"
      justify="space-evenly"
    >
      {/* Title */}
      <Text
        fontWeight="700"
        fontSize="15px"
        color="gray.800"
        textAlign="center"
        mb={1}
      >
        {name}
      </Text>

      {/* Description - Choices */}
      <Text
        fontSize="13px"
        textAlign="center"
        fontWeight="600"
        color="gray.700"
        mb={0.5}
      >
        <Text as="span" fontSize="15px" fontWeight="800" color="red.700">
          {choices}
        </Text>{" "}
        {choices === 1 ? "Choice" : "Choices"}
      </Text>

      {/* Extras if any */}
      {extras && (
        <VStack
          align="center"
          spacing={0}
          mb={1}
          fontSize="12px"
          color="gray.600"
        >
          {extras.map((extra) => (
            <Text key={extra}>+ {extra}</Text>
          ))}
        </VStack>
      )}

      {/* Price */}
      <Text fontWeight="800" fontSize="18px" color="red.700" textAlign="center">
        ${price.toFixed(2)}
      </Text>
    </Flex>
  );
}

// Combo For One Section
function ComboForOneSection() {
  const dinnerOneItems = [
    { item: "Ginger Beef", number: "Q1", spicy: true },
    { item: "Sweet & Sour Pork", number: "Q2", spicy: false },
    { item: "Lemon Chicken", number: "Q3", spicy: false },
    { item: "Dry Garlic Spareribs", number: "Q4", spicy: false },
    { item: "Chicken Balls", number: "Q5", spicy: false },
    { item: "Deep Fried Bubble Shrimp", number: "Q6", spicy: false },
    { item: "Chicken Chop Suey", number: "Q7", spicy: false },
    { item: "Chicken w/ Mixed Veggies", number: "Q8", spicy: false },
    { item: "Stir Fried Mixed Veggies", number: "Q9", spicy: false },
  ];

  return (
    <Flex direction="column" h="100%">
      <VStack spacing={4} p={5} flex="1">
        {/* Header */}
        <Box textAlign="center" mb={1}>
          <Text
            fontSize="26px"
            fontWeight="800"
            color="red.700"
            letterSpacing="wide"
            textTransform="uppercase"
          >
            Combo For One
          </Text>
          <Divider
            borderColor="red.600"
            borderWidth="2px"
            mt={2}
            width="60%"
            mx="auto"
          />
        </Box>

        {/* Included Items */}
        <Box
          border="2px solid"
          borderColor="red.600"
          borderRadius="md"
          p={2}
          w="full"
        >
          <Text
            fontSize="16px"
            fontWeight="700"
            color="red.700"
            mb={2}
            textAlign="center"
          >
            Combo Includes
          </Text>
          <VStack spacing={1} align="start" pl={2}>
            {["Spring Roll (1)", "Chicken Fried Rice"].map((item) => (
              <HStack key={item} spacing={2}>
                <Box w="6px" h="6px" bg="red.600" borderRadius="full" />
                <Text fontSize="15px" color="gray.800">
                  {item}
                </Text>
              </HStack>
            ))}
          </VStack>
        </Box>

        {/* Pricing Options */}
        <VStack spacing={2} w="full">
          <Box
            border="2px solid"
            borderColor="red.600"
            borderRadius="md"
            p={3}
            w="full"
            textAlign="center"
          >
            <Text fontSize="15px" fontWeight="600" mb={1} color="gray.800">
              <Text as="span" fontSize="18px" fontWeight="800" color="red.700">
                1
              </Text>{" "}
              Choice
            </Text>
            <Text fontWeight="800" fontSize="22px" color="red.700">
              $13.96
            </Text>
          </Box>

          <Box
            border="2px solid"
            borderColor="red.600"
            borderRadius="md"
            p={3}
            w="full"
            textAlign="center"
          >
            <Text fontSize="15px" fontWeight="600" mb={1} color="gray.800">
              <Text as="span" fontSize="18px" fontWeight="800" color="red.700">
                2
              </Text>{" "}
              Choices
            </Text>
            <Text fontWeight="800" fontSize="22px" color="red.700">
              $15.96
            </Text>
          </Box>

          <Box
            border="2px solid"
            borderColor="red.600"
            borderRadius="md"
            p={3}
            w="full"
            textAlign="center"
          >
            <Text fontSize="15px" color="gray.700" mb={1}>
              Extra Side Order
            </Text>
            <Text fontWeight="800" fontSize="20px" color="red.700">
              $6.96
            </Text>
          </Box>
        </VStack>

        {/* Choices List */}
        <Box
          border="2px solid"
          borderColor="red.600"
          borderRadius="md"
          p={3}
          w="full"
        >
          <Text
            fontWeight="700"
            fontSize="17px"
            color="red.700"
            mb={2}
            textAlign="center"
            textTransform="uppercase"
            letterSpacing="wide"
          >
            Combo Choices
          </Text>
          <VStack spacing={1} align="start">
            {dinnerOneItems.map((item) => (
              <HStack key={item.number} spacing={2} w="full">
                <Text
                  fontSize="15px"
                  fontWeight="600"
                  color="red.600"
                  minW="30px"
                >
                  {item.number}
                </Text>
                <Text fontSize="15px" color="gray.800">
                  {item.item}
                </Text>
                {item.spicy && <FaPepperHot color="#DC2626" size={12} />}
              </HStack>
            ))}
          </VStack>
        </Box>
      </VStack>

      {/* Footer */}
      <Box borderTop="2px solid" borderColor="gray.300" p={3}>
        <VStack spacing={1} fontSize="13px" color="gray.600">
          <Text fontStyle="italic">
            Extra Charges For Substitutions Not On List
          </Text>
          <Text fontStyle="italic">
            Prices Subject to Change Without Notice
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
}

// Restaurant Info Section
function RestaurantInfoSection() {
  return (
    <VStack spacing={4} p={5} align="stretch">
      {/* Logo & Name */}
      <Box textAlign="center" mb={1}>
        <HStack justify="center" spacing={3} mb={2}>
          <Image
            width="55px"
            objectFit="contain"
            src="logored.png"
            alt="Ken's Kowloon Kitchen Logo"
          />
          <VStack align="start" spacing={0}>
            <Text
              fontSize="22px"
              fontWeight="800"
              color="red.700"
              fontFamily="langar"
              lineHeight="1.1"
            >
              Ken's Kowloon Kitchen
            </Text>
            <Text
              fontSize="22px"
              color="red.700"
              fontFamily="chinese"
              fontWeight="700"
              lineHeight="1"
            >
              九龙冰室
            </Text>
          </VStack>
        </HStack>
        <Divider
          borderColor="red.600"
          borderWidth="2px"
          width="70%"
          mx="auto"
        />
      </Box>

      {/* Service Options */}
      <HStack
        justify="center"
        spacing={3}
        fontSize="17px"
        fontWeight="600"
        color="gray.700"
      >
        <Text>Pick Up</Text>
        <Box w="2px" h="4px" bg="red.600" borderRadius="full" />
        <Text>Delivery</Text>
        <Box w="2px" h="4px" bg="red.600" borderRadius="full" />
        <Text>Dine-in</Text>
      </HStack>

      {/* Contact Info */}
      <VStack spacing={2}>
        <HStack spacing={2} color="red.700" fontWeight="700">
          <FaPhone size={14} />
          <Text fontSize="16px">403-279-4471 | 403-279-4472</Text>
        </HStack>
        <HStack spacing={2} color="gray.700">
          <FaMapMarkerAlt size={14} color="#B91C1C" />
          <VStack spacing={0} align="start">
            <Text fontSize="15px" fontWeight="600">
              2118 Glenmore Court SE
            </Text>
            <Text fontSize="13px" color="gray.500">
              By Glenmore Inn Hotel
            </Text>
          </VStack>
        </HStack>
        <HStack spacing={2} color="red.700">
          <FaGlobe size={14} />
          <VStack spacing={0} align="start">
            <Text fontSize="15px" fontWeight="700">
              kenskowloonkitchen.com
            </Text>
            <Text fontSize="13px" color="gray.600">
              Order Pickup Online
            </Text>
          </VStack>
        </HStack>
      </VStack>

      {/* Business Hours */}
      <InfoCard title="Business Hours">
        <VStack spacing={2} align="stretch">
          <Box>
            <Text fontWeight="700" fontSize="17px" color="red.700" mb={1}>
              Lunch
            </Text>
            <InfoRow label="Monday - Friday" value="11:00AM - 2:00PM" />
            <InfoRow label="Saturday - Sunday" value="Closed" />
          </Box>
          <Divider borderColor="gray.300" />
          <Box>
            <Text fontWeight="700" fontSize="17px" color="red.700" mb={1}>
              Dinner
            </Text>
            <InfoRow label="Monday - Sunday" value="3:30PM - 9:00PM" />
          </Box>
        </VStack>
      </InfoCard>

      {/* Promotion */}
      <Box border="2px solid" borderColor="orange.400" borderRadius="md" p={3}>
        <Text
          fontWeight="800"
          fontSize="16px"
          color="orange.700"
          textAlign="center"
          mb={1}
        >
          SPECIAL OFFER
        </Text>
        <Text
          textAlign="center"
          fontSize="15px"
          color="gray.800"
          fontWeight="600"
        >
          <Text as="span" fontWeight="800" color="red.700">
            5% OFF
          </Text>{" "}
          Pick-Up Orders Over{" "}
          <Text as="span" fontWeight="800" color="red.700">
            $40
          </Text>
        </Text>
      </Box>

      {/* Delivery Info */}
      <InfoCard title="Free Delivery">
        <VStack spacing={2.5} align="stretch">
          <Box>
            <Text fontSize="14px" color="gray.700" mb={1} textAlign="center">
              Orders over{" "}
              <Text as="span" fontWeight="800" color="red.700">
                $40
              </Text>
            </Text>
            <Text
              fontSize="15px"
              fontWeight="600"
              color="red.600"
              textAlign="center"
              lineHeight="1.4"
            >
              Ogden, Lynnwood, Riverbend, Quarry Park, Douglasglen
            </Text>
          </Box>
          <Divider borderColor="gray.300" />
          <Box>
            <Text fontSize="14px" color="gray.700" mb={1} textAlign="center">
              Orders over{" "}
              <Text as="span" fontWeight="800" color="red.700">
                $60
              </Text>
            </Text>
            <Text
              fontSize="15px"
              fontWeight="600"
              color="red.600"
              textAlign="center"
              lineHeight="1.4"
            >
              Douglasdale, McKenzie, New Brighton, South View, Dover, Erin Woods
            </Text>
          </Box>
        </VStack>
      </InfoCard>

      {/* QR Code & Catering */}
      <HStack
        spacing={2}
        border="2px solid"
        borderColor="gray.300"
        borderRadius="md"
        p={2}
      >
        <QRCode size={50} value="http://kenskowloonkitchen.com/" />
        <VStack align="start" spacing={0} fontSize="15px" flex="1">
          <Text fontWeight="700" color="red.700">
            Catering Available
          </Text>
          <Text color="gray.600">To Any Party Size</Text>
          <Text color="gray.600">Contact Us For More Info</Text>
        </VStack>
      </HStack>
    </VStack>
  );
}

// Reusable info card component
function InfoCard({ title, children }) {
  return (
    <Box
      border="2px solid"
      borderColor="red.600"
      borderRadius="md"
      paddingLeft={2}
      paddingRight={2}
      paddingTop={1}
      paddingBottom={1}
    >
      <Text
        fontWeight="700"
        fontSize="16px"
        color="red.700"
        textAlign="center"
        mb={2}
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {title}
      </Text>
      {children}
    </Box>
  );
}

// Reusable info row component
function InfoRow({ label, value }) {
  return (
    <HStack justify="space-between" fontSize="16px" color="gray.700">
      <Text>{label}</Text>
      <Text fontWeight="600">{value}</Text>
    </HStack>
  );
}
